import Alert, { AlertProps } from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Close from '@mui/icons-material/Close'
import { observer } from 'mobx-react-lite'
import { useThemeStore } from 'theme/ThemeStore'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import useStore from 'reusable/mobx-store-utils'
import GapAlertStore from './GapAlertStore'
import Typography from '@mui/material/Typography'

export interface GapAlertAction {
   title: string
   onClick: () => void
   loading?: boolean
   startIcon?: React.ReactNode
   endIcon?: React.ReactNode
   variant?: 'outlined' | 'contained' | 'text'
}

export interface GapAlertProps extends Omit<AlertProps, 'action' | 'severity' | 'variant'> {
   title?: string
   subTitle?: string
   action?: GapAlertAction
   severity: 'info' | 'success' | 'warning' | 'error'
   variant?: 'standard' | 'filled' | 'outlined'
}

export default observer(function GapAlert({
   severity,
   variant = 'standard',
   title,
   subTitle,
   icon,
   sx,
   children,
   action,
   onClose,
   ...rest
}: GapAlertProps) {
   const { dark } = useThemeStore()
   const gapAlertStore = useStore(sp => new GapAlertStore(sp), { dark, severity, variant })

   const combinedAction = (action || onClose)
      ? <Stack direction="row" spacing={1} alignItems="center">
         {action && <GapAlertActionButton action={action} />}
         {onClose && <GapAlertCloseButton onClose={onClose} />}
      </Stack>
      : undefined

   return <Alert
      severity={severity}
      variant={variant}
      icon={icon ?? <gapAlertStore.Icon fontSize="inherit" sx={{ color: 'inherit' }} />}
      action={combinedAction}
      sx={{
         borderRadius: '8px',
         px: 2,
         py: 0.75,
         '& .MuiAlertTitle-root': { color: 'inherit', mb: 0.5 },
         '& .MuiAlert-icon': { color: 'inherit', alignSelf: 'flex-start', mr: 1.5 },
         '& .MuiAlert-action': { alignItems: 'center', pt: 0 },
         cursor: rest.onClick ? 'pointer' : 'inherit',
         ...gapAlertStore.variantSx,
         ...sx,
      }}
      {...rest}
   >
      {title && <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>}
      {subTitle && <Typography variant="body2">{subTitle}</Typography>}
      {typeof rest.content === 'string' && <Typography variant="body2">{rest.content}</Typography>}
      {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
   </Alert>
})

const GapAlertActionButton = observer(function GapAlertActionButton({ action }: { action: GapAlertAction }) {
   const endIcon = action.loading
      ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
      : action.endIcon
   return <Button
      size="small"
      color="inherit"
      variant={action.variant ?? 'outlined'}
      disabled={action.loading}
      onClick={action.onClick}
      startIcon={action.startIcon}
      endIcon={endIcon}
   >
      {action.title}
   </Button>
})

const GapAlertCloseButton = observer(function GapAlertCloseButton({ onClose }: { onClose: (event: React.SyntheticEvent) => void }) {
   return <IconButton size="small" aria-label="Close" onClick={onClose} sx={{ color: 'inherit' }}>
      <Close fontSize="small" />
   </IconButton>
})